import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getElectronAPI, isDesktopApp } from '@/desktop/electronApi';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import { useAuthStore } from '@/store/authStore';
import { setApiBaseUrl } from '@/services/api';

/**
 * Desktop boot gate:
 * - Apply API base from Electron config
 * - Sync setupComplete / boundCompany from config → session store
 * - First install (no enrollments, !setupComplete) → /login
 * - Setup incomplete + authenticated → /desktop-setup
 * - Enrolled + not unlocked → /unlock
 */
export default function DesktopAuthGate({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(!isDesktopApp());
  const [enrollmentCount, setEnrollmentCount] = useState(0);
  const locked = useDesktopSessionStore((s) => s.locked);
  const locallyUnlocked = useDesktopSessionStore((s) => s.locallyUnlocked);
  const setupComplete = useDesktopSessionStore((s) => s.setupComplete);
  const setSetupState = useDesktopSessionStore((s) => s.setSetupState);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();

  useEffect(() => {
    if (!isDesktopApp()) {
      setReady(true);
      return;
    }
    void (async () => {
      const api = getElectronAPI();
      if (!api) {
        setReady(true);
        return;
      }
      try {
        const cfg = await api.getConfig();
        if (cfg?.apiBaseUrl) setApiBaseUrl(cfg.apiBaseUrl);
        setSetupState({
          setupComplete: Boolean(cfg.setupComplete),
          boundCompanyCode: cfg.boundCompanyCode || null,
          desktopSyncMode: cfg.desktopSyncMode || null,
          pendingDesktopSetup: !cfg.setupComplete,
        });
        const count = await api.vault.enrollmentCount(cfg.boundCompanyCode || undefined);
        setEnrollmentCount(count);
      } catch {
        setEnrollmentCount(0);
      } finally {
        setReady(true);
      }
    })();
  }, [setSetupState]);

  if (!ready) return null;

  if (!isDesktopApp()) return <>{children}</>;

  const path = location.pathname;
  const onAuthPages =
    path === '/login' ||
    path === '/unlock' ||
    path === '/signup' ||
    path === '/reset-password' ||
    path === '/desktop-setup';

  // Cold start with enrolled cashiers — always require unlock (allow /login for forgot-PIN).
  if (enrollmentCount > 0 && setupComplete && (locked || !locallyUnlocked)) {
    if (path !== '/unlock' && path !== '/login') {
      return <Navigate to="/unlock" replace />;
    }
  }

  // First install: no enrollments and setup not done → login
  if (!setupComplete && enrollmentCount === 0 && path === '/unlock') {
    return <Navigate to="/login" replace />;
  }

  // Authenticated but wizard incomplete → force setup (except auth pages already allowed)
  if (isAuthenticated && !setupComplete && path !== '/desktop-setup' && path !== '/login') {
    return <Navigate to="/desktop-setup" replace />;
  }

  // Unauthenticated first install hitting deep links → login
  if (!isAuthenticated && !setupComplete && enrollmentCount === 0 && !onAuthPages) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
